import { Action, Dispatch } from 'redux';

import * as Actions from '../actions/common';
import errors from '../errors';
import i18n from '../resources/i18n';
import Transfers, { IOptions } from '../utils/Transfers';

/**
 * シナリオ抽象クラス
 */
export default abstract class AbstractScenario<T, R = any, P = any> {
	protected readonly dispatch: Dispatch<Action<P | {}>>;
	protected readonly token: string;

	constructor(dispatch: Dispatch<Action<P | {}>>, token: string) {
		this.dispatch = dispatch;
		this.token = token;
	}

	/**
	 * シナリオ実行
	 * @param arg シナリオ引数
	 */
	public run = async (arg: T) => {
		try {
			return this.process(arg);
		} catch (e) {
			return this.logError(e);
		}
	};

	/**
	 * シナリオ実行
	 * @param arg シナリオ引数
	 */
	public runThen = (arg: T, func: any) => {
		return this.process(arg)
			.then(func)
			.catch((e: Error) => this.logError(e));
	};

	/**
	 * シナリオ実処理
	 * @param arg シナリオ引数
	 */
	protected abstract process(arg: T): Promise<R>;

	/**
	 * Web API Read実行
	 * @param modelId モデルID
	 * @param data 送信データ
	 */
	protected read = <U = any>(
		modelId: string,
		data: IOptions = {}
	): Promise<U> => {
		const error = new errors.ServerSystem();
		return Transfers.read(modelId, this.token, data)
			.then(response => response.json())
			.then(this.errorHandler(error))
			.then(receiveData => receiveData[modelId]);
	};

	/**
	 * Web API Update実行
	 * @param modelId モデルID
	 * @param data 送信データ
	 */
	protected update = <U = any>(
		modelId: string,
		data: IOptions = {}
	): Promise<U> => {
		const error = new errors.ServerSystem();
		return Transfers.update(modelId, this.token, data)
			.then(response => response.json())
			.then(this.errorHandler(error))
			.then(receiveData => receiveData[modelId]);
	};

	/**
	 * Web API Create実行
	 * @param modelId モデルID
	 * @param data 送信データ
	 */
	protected create = <U = any>(
		modelId: string,
		data: IOptions = {}
	): Promise<U> => {
		const error = new errors.ServerSystem();
		return Transfers.create(modelId, this.token, data)
			.then(response => response.json())
			.then(this.errorHandler(error))
			.then(receiveData => receiveData[modelId]);
	};

	/**
	 * Web API Delete実行
	 * @param modelId モデルID
	 * @param data 送信データ
	 */
	protected delete = <U = any>(
		modelId: string,
		data: IOptions = {}
	): Promise<U> => {
		const error = new errors.ServerSystem();
		return Transfers.delete(modelId, this.token, data)
			.then(response => response.json())
			.then(this.errorHandler(error))
			.then(receiveData => receiveData[modelId]);
	};

	/**
	 * エラーハンドラ
	 */
	protected errorHandler = (e: Error) => (receiveData: any): any => {
		if (!receiveData || typeof receiveData !== 'object') {
			// フォーマット異常
		}
		if (receiveData._tsqDataMap) {
			// コアエラー
			this.handleCoreError(receiveData._tsqDataMap, e);
		}
		if (receiveData.tsqErrorMessage) {
			// 業務エラーメッセージ表示
			this.handleGeneralError(receiveData.tsqErrorMessage, e);
			throw e;
		}

		return receiveData;
	};

	/**
	 * コアエラーハンドラ
	 */
	protected handleCoreError = (tsqDataMap: any, e: Error) => {
		const { responseCd } = tsqDataMap;
		if (responseCd === '001') {
			// 認証エラー
			e.message = i18n.t('AbstractScenario_0001');
			this.dispatch(Actions.commonHandleAuthError({}));
			throw e;
		} else if (responseCd === '002') {
			// セッションタイムアウト
			e.message = i18n.t('AbstractScenario_0002');
			this.dispatch(Actions.commonHandleSessionTimeout({}));
			throw e;
		} else if (responseCd === '003') {
			// 排他エラー
			e.message = i18n.t('AbstractScenario_0003');
			this.dispatch(Actions.commonHandleExclusionError({}));
			throw e;
		}
	};

	/**
	 * 一般エラーハンドラ
	 */
	protected handleGeneralError = (tsqErrorMessage: any, e: Error) => {
		if (Array.isArray(tsqErrorMessage)) {
			const map = tsqErrorMessage[0];
			this.dispatch(
				Actions.commonHandleShowErrorMessage({
					content: map.message,
					replacements: map.strArray,
				})
			);
			e.message = this.formatMessage(map.message, map.strArray);
		} else if (typeof tsqErrorMessage === 'object') {
			this.dispatch(
				Actions.commonHandleShowErrorMessage({
					content: tsqErrorMessage.message,
					replacements: tsqErrorMessage.strArray,
				})
			);
			e.message = this.formatMessage(
				tsqErrorMessage.message,
				tsqErrorMessage.strArray
			);
		}
	};

	/**
	 * エラーログ送信
	 */
	protected logError = (e: Error) => {
		// tslint:disable-next-line: no-console
		console.error(e);
		const timestamp = new Date();
		const log = {
			level: 'ERROR',
			message: JSON.stringify({
				message: String(e),
			}),
			timestamp: timestamp.getTime(),
		};
		const param = {
			logData: [JSON.stringify(log)],
		};
		Transfers.create('tsq_common_ClientLogModel', this.token, param).catch(
			ex => {
				// tslint:disable-next-line: no-console
				console.error(ex);
			}
		);
	};

	/**
	 * メッセージフォーマット
	 */
	protected formatMessage = (
		content: string,
		replacements?: string[]
	): string => {
		let message = i18n.t(content, { ns: 'message' });
		if (replacements) {
			replacements.map((replacement: string, index: number) => {
				message = message.replace('{' + index + '}', replacement);
			});
		}
		return message;
	};
}



// WEBPACK FOOTER //
// ./src/scenario/AbstractScenario.ts